import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  TextField,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import PreviewIcon from '@mui/icons-material/Preview';
import EditIcon from '@mui/icons-material/Edit';
import FieldPalette from '../../components/print/FieldPalette';
import InteractiveHeaderPreview from '../../components/print/InteractiveHeaderPreview';
import { toast } from 'react-hot-toast';
import customPrintTemplateService from '../../services/customPrintTemplateService';
import printSettingsService from '../../services/printSettingsService';
import companySettingsService from '../../services/companySettingsService';

const getPaperSizeFromBaseFormat = (baseFormat) => {
  if (baseFormat.startsWith('3_INCH')) {
    return '3_INCH';
  } else if (baseFormat.startsWith('2_INCH')) {
    return '2_INCH';
  } else if (baseFormat.startsWith('A6')) {
    return 'A6';
  } else if (baseFormat.startsWith('A5')) {
    return 'A5';
  } else {
    return 'A4';
  }
};

const PrintFormatCustomizer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const baseFormat = searchParams.get('baseFormat') || 'A4';
  const templateId = searchParams.get('templateId');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [templateConfig, setTemplateConfig] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [previewTemplateId, setPreviewTemplateId] = useState(null);
  const [previewMode, setPreviewMode] = useState('pdf'); // 'pdf' or 'interactive'
  const [companySettings, setCompanySettings] = useState(null);
  const previewUpdateTimeoutRef = useRef(null);

  // Load template or create from base format
  useEffect(() => {
    loadTemplate();
    loadCompanySettings();
  }, [baseFormat, templateId]);

  const loadCompanySettings = async () => {
    try {
      const settings = await companySettingsService.getSettings();
      setCompanySettings(settings);
    } catch (error) {
      console.error('Error loading company settings:', error);
      // Set default structure if API fails
      setCompanySettings({
        name: 'Company Name',
        addressLine1: 'Address Line 1',
        addressLine2: '',
        contact: 'Contact Number',
        email: 'email@example.com',
        uen: 'GST Number',
        logoUrl: ''
      });
    }
  };

  const loadTemplate = async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      if (templateId) {
        // Load existing template
        try {
          const template = await customPrintTemplateService.getTemplateById(parseInt(templateId));
          setCurrentTemplate(template);
          // Ensure templateConfig is properly structured
          if (template.templateConfig && typeof template.templateConfig === 'object') {
            setTemplateConfig(template.templateConfig);
          } else {
            console.warn('Template config is not properly structured, using default');
            const defaultStructure = await customPrintTemplateService.getDefaultFormatStructure(baseFormat);
            setTemplateConfig(defaultStructure);
          }
          setTemplateName(template.templateName);
        } catch (error) {
          console.error('Error loading template by ID:', error);
          // If template not found, fall back to creating from base format
          const defaultStructure = await customPrintTemplateService.getDefaultFormatStructure(baseFormat);
          setTemplateConfig(defaultStructure);
          setTemplateName(`Custom ${baseFormat.replace(/_/g, ' ')}`);
        }
      } else {
        // Check if there's an existing template for this base format
        try {
          const existingTemplates = await customPrintTemplateService.getTemplatesByBaseFormat(baseFormat);
          // Find the most recent template or active one
          let existingTemplate = existingTemplates.find(t => t.isActive) ||
            existingTemplates.sort((a, b) =>
              new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
            )[0];

          if (existingTemplate) {
            // Load existing template with saved config
            setCurrentTemplate(existingTemplate);
            // Ensure templateConfig is properly structured and has the expected structure
            if (existingTemplate.templateConfig && typeof existingTemplate.templateConfig === 'object') {
              // Validate that templateConfig has the expected structure
              const config = existingTemplate.templateConfig;
              if (config.itemsTable && config.itemsTable.columns && Array.isArray(config.itemsTable.columns)) {
                // Config looks valid, use it
                setTemplateConfig(config);
              } else {
                console.warn('Template config structure is invalid, using default');
                const defaultStructure = await customPrintTemplateService.getDefaultFormatStructure(baseFormat);
                setTemplateConfig(defaultStructure);
              }
            } else {
              console.warn('Existing template config is not properly structured, using default');
              const defaultStructure = await customPrintTemplateService.getDefaultFormatStructure(baseFormat);
              setTemplateConfig(defaultStructure);
            }
            setTemplateName(existingTemplate.templateName);
            // Update URL to include templateId for future loads
            navigate(`/settings/print/customize?baseFormat=${baseFormat}&templateId=${existingTemplate.id}`, { replace: true });
          } else {
            // No existing template, create new from base format
            const defaultStructure = await customPrintTemplateService.getDefaultFormatStructure(baseFormat);
            setTemplateConfig(defaultStructure);
            setTemplateName(`Custom ${baseFormat.replace(/_/g, ' ')}`);
          }
        } catch (error) {
          console.error('Error checking for existing templates:', error);
          // Fallback to default structure
          const defaultStructure = await customPrintTemplateService.getDefaultFormatStructure(baseFormat);
          setTemplateConfig(defaultStructure);
          setTemplateName(`Custom ${baseFormat.replace(/_/g, ' ')}`);
        }
      }
    } catch (error) {
      console.error('Error loading template:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to load template';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = useCallback((newConfig) => {
    setTemplateConfig(newConfig);
    // Auto-update preview when config changes (debounced)
    if (previewUpdateTimeoutRef.current) {
      clearTimeout(previewUpdateTimeoutRef.current);
    }
    previewUpdateTimeoutRef.current = setTimeout(() => {
      generatePreviewFromConfig(newConfig);
    }, 1500); // Wait 1.5 seconds after last change
  }, []);


  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    try {
      setSaving(true);
      setErrorMessage('');

      const templateData = {
        templateName: templateName.trim(),
        paperSize: getPaperSizeFromBaseFormat(baseFormat),
        baseFormat: baseFormat,
        templateConfig: templateConfig,
        isActive: false
      };

      let savedTemplate;
      if (currentTemplate) {
        // Update existing template
        savedTemplate = await customPrintTemplateService.updateTemplate(
          currentTemplate.id,
          templateData
        );
      } else {
        // Create new template
        savedTemplate = await customPrintTemplateService.createTemplate(templateData);
      }

      toast.success('Template saved successfully!');

      // Update current template
      setCurrentTemplate(savedTemplate);

      // Clean up preview template if it exists
      if (previewTemplateId && previewTemplateId !== savedTemplate.id) {
        try {
          await customPrintTemplateService.deleteTemplate(previewTemplateId);
        } catch (error) {
          // Ignore 404 errors - template may have already been deleted
          if (error.response?.status !== 404) {
            console.error('Error deleting preview template:', error);
          }
        }
        setPreviewTemplateId(null);
      }

      // Generate preview with the saved custom template
      try {
        const pdfBlob = await customPrintTemplateService.previewTemplate(savedTemplate.id);
        const url = window.URL.createObjectURL(pdfBlob);
        if (previewUrl) {
          window.URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(url);
      } catch (error) {
        console.error('Error generating preview after save:', error);
      }

      // Ask user if they want to activate this template
      if (window.confirm('Template saved! Do you want to activate it for printing?')) {
        try {
          await customPrintTemplateService.activateTemplate(savedTemplate.id);
          toast.success('Template activated! It will now be used for all print operations.');
        } catch (error) {
          console.error('Error activating template:', error);
          toast.error('Template saved but could not be activated.');
        }
      }
    } catch (error) {
      console.error('Error saving template:', error);
      const errorMsg = error.response?.data?.message || 'Failed to save template';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Generate preview using custom config - create/update temp template for preview
  const generatePreviewFromConfig = useCallback(async (config) => {
    if (!config) return;

    try {
      setPreviewing(true);
      setErrorMessage('');

      let templateId = currentTemplate?.id || previewTemplateId;

      // If we have a current template, update it
      if (currentTemplate?.id) {
        try {
          await customPrintTemplateService.updateTemplate(currentTemplate.id, {
            templateName: currentTemplate.templateName,
            paperSize: currentTemplate.paperSize,
            baseFormat: currentTemplate.baseFormat,
            templateConfig: config,
            isActive: currentTemplate.isActive
          });
          templateId = currentTemplate.id;
        } catch (error) {
          // If template not found (404), clear it and create a new preview template
          if (error.response?.status === 404) {
            console.warn('Current template not found, creating new preview template');
            setCurrentTemplate(null);
            // Fall through to create new preview template
          } else {
            throw error;
          }
        }
      }

      // If we don't have a template ID yet, try to use preview template or create new one
      if (!templateId) {
        if (previewTemplateId) {
          try {
            // Update existing preview template
            await customPrintTemplateService.updateTemplate(previewTemplateId, {
              templateName: `Preview ${Date.now()}`,
              paperSize: getPaperSizeFromBaseFormat(baseFormat),
              baseFormat: baseFormat,
              templateConfig: config,
              isActive: false
            });
            templateId = previewTemplateId;
          } catch (error) {
            // If preview template not found (404), clear it and create a new one
            if (error.response?.status === 404) {
              console.warn('Preview template not found, creating new preview template');
              setPreviewTemplateId(null);
              // Fall through to create new preview template
            } else {
              throw error;
            }
          }
        }
      }

      // Create new preview template if we still don't have a template ID
      if (!templateId) {
        // Create new preview template
        try {
          const savedTemplate = await customPrintTemplateService.createFromBaseFormat({
            templateName: `Preview ${Date.now()}`,
            baseFormat: baseFormat
          });
          templateId = savedTemplate.id;
          setPreviewTemplateId(templateId);

          // Update with current config
          await customPrintTemplateService.updateTemplate(templateId, {
            templateName: `Preview ${Date.now()}`,
            paperSize: getPaperSizeFromBaseFormat(baseFormat),
            baseFormat: baseFormat,
            templateConfig: config,
            isActive: false
          });
        } catch (error) {
          console.error('Error creating preview template:', error);
          // If creation fails, try to find and reuse existing preview template
          try {
            const allTemplates = await customPrintTemplateService.getAllTemplates();
            const previewTemplate = allTemplates.find(t =>
              t.templateName?.startsWith('Preview') &&
              t.baseFormat === baseFormat &&
              !t.isActive
            );
            if (previewTemplate) {
              templateId = previewTemplate.id;
              setPreviewTemplateId(templateId);
              await customPrintTemplateService.updateTemplate(templateId, {
                templateName: previewTemplate.templateName,
                paperSize: previewTemplate.paperSize,
                baseFormat: previewTemplate.baseFormat,
                templateConfig: config,
                isActive: false
              });
            } else {
              // If no preview template found, create a temporary one without saving
              throw new Error('Unable to create preview template. Please try again.');
            }
          } catch (innerError) {
            console.error('Error finding existing preview template:', innerError);
            throw error;
          }
        }
      }

      // Generate preview using the template
      const pdfBlob = await customPrintTemplateService.previewTemplate(templateId);

      // Check if response is actually a PDF (starts with PDF header) or an error JSON
      if (pdfBlob instanceof Blob && pdfBlob.type === 'application/pdf') {
        const url = window.URL.createObjectURL(pdfBlob);

        if (previewUrl) {
          window.URL.revokeObjectURL(previewUrl);
        }

        setPreviewUrl(url);
        setErrorMessage('');
      } else {
        // Try to read as text to see error message
        const text = await pdfBlob.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.message || 'Failed to generate preview');
        } catch (e) {
          throw new Error('Failed to generate preview. Server returned invalid response.');
        }
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      let errorMsg = 'Failed to generate preview';
      if (error.response) {
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const errorData = JSON.parse(text);
            errorMsg = errorData.message || errorMsg;
          } catch (e) {
            errorMsg = error.response.statusText || errorMsg;
          }
        } else {
          errorMsg = error.response.data?.message || error.response.statusText || errorMsg;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setPreviewing(false);
    }
  }, [currentTemplate, previewTemplateId, baseFormat, previewUrl]);

  const initializePreview = useCallback(async () => {
    if (!templateConfig) return;
    await generatePreviewFromConfig(templateConfig);
  }, [templateConfig, generatePreviewFromConfig]);

  // Initialize preview when template config is loaded
  useEffect(() => {
    if (templateConfig && !previewUrl) {
      initializePreview();
    }
  }, [templateConfig, initializePreview, previewUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
      if (previewUpdateTimeoutRef.current) {
        clearTimeout(previewUpdateTimeoutRef.current);
      }
      // Clean up preview template if it exists and is not the current template
      if (previewTemplateId && previewTemplateId !== currentTemplate?.id) {
        customPrintTemplateService.deleteTemplate(previewTemplateId).catch(err => {
          // Ignore 404 errors - template may have already been deleted
          if (err.response?.status !== 404) {
            console.error('Error deleting preview template on unmount:', err);
          }
        });
      }
    };
  }, [previewUrl, previewTemplateId, currentTemplate]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Top Header Bar */}
      <Paper elevation={2} sx={{ p: 2, borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/settings/print')}
              variant="outlined"
              size="small"
            >
              Back
            </Button>
            <Typography variant="h6" component="h1">
              Customize Print Format
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Template Name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Enter template name"
              required
              sx={{ width: 250 }}
            />
            <Button
              variant="outlined"
              startIcon={previewing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={() => generatePreviewFromConfig(templateConfig)}
              disabled={previewing || !templateConfig}
              size="small"
            >
              Refresh Preview
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || !templateConfig || !templateName.trim()}
              size="small"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Error Message */}
      {errorMessage && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      {/* Main Content - Split View */}
      <Grid container sx={{ flex: 1, overflow: 'hidden', m: 0 }}>
        {/* Left Side - Field Palette */}
        <Grid item xs={12} md={3} sx={{ borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', minHeight: 0 }}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <FieldPalette
              fields={templateConfig}
              companySettings={companySettings}
              onFooterImageUpload={async (file) => {
                const result = await companySettingsService.uploadFooterImage(file);
                return result;
              }}
              onCompanySettingsUpdate={(updated) => {
                const prevUrl = companySettings?.footerImageUrl;
                setCompanySettings(updated);
                // Refresh preview when footer image changes so new image appears in PDF
                if (updated?.footerImageUrl && updated.footerImageUrl !== prevUrl && templateConfig) {
                  generatePreviewFromConfig(templateConfig);
                }
              }}
              onFieldClick={(field) => {
                // Toggle field when clicked
                if (!templateConfig) return;

                const newConfig = JSON.parse(JSON.stringify(templateConfig));
                const { category, field: fieldName } = field;

                if (category === 'Items Table') {
                  const columns = newConfig.itemsTable?.columns || [];
                  const columnIndex = columns.findIndex(col => col.field === fieldName);
                  if (columnIndex >= 0) {
                    columns[columnIndex].enabled = !columns[columnIndex].enabled;
                    newConfig.itemsTable.columns = columns;
                  }
                } else if (category === 'Header' || category === 'Order Info' || category === 'Summary' || category === 'Footer') {
                  const sectionKey = category === 'Order Info' ? 'orderInfo' : category.toLowerCase();
                  if (!newConfig[sectionKey]) {
                    newConfig[sectionKey] = {};
                  }
                  if (!newConfig[sectionKey][fieldName]) {
                    newConfig[sectionKey][fieldName] = { enabled: false };
                  }
                  newConfig[sectionKey][fieldName].enabled = !newConfig[sectionKey][fieldName].enabled;
                }

                handleConfigChange(newConfig);
              }}
              onColumnReorder={(newColumns) => {
                // Reorder columns in items table
                if (!templateConfig) return;

                const newConfig = JSON.parse(JSON.stringify(templateConfig));
                newConfig.itemsTable.columns = newColumns;
                handleConfigChange(newConfig);
              }}
            />
          </Box>
        </Grid>

        {/* Right Side - Live Preview */}
        <Grid item xs={12} md={9} sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Paper sx={{ flex: 1, m: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Live Preview
                </Typography>
                <ToggleButtonGroup
                  value={previewMode}
                  exclusive
                  onChange={(e, newMode) => {
                    if (newMode !== null) setPreviewMode(newMode);
                  }}
                  size="small"
                >
                  <ToggleButton value="pdf">
                    <Tooltip title="PDF Preview">
                      <PreviewIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="interactive">
                    <Tooltip title="Interactive Header Editor">
                      <EditIcon fontSize="small" />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {previewing && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Updating preview...
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {previewMode === 'interactive' ? (
                <Box sx={{ flex: 1, overflow: 'auto', p: 2, minHeight: 0 }}>
                  <InteractiveHeaderPreview
                    templateConfig={templateConfig}
                    companySettings={companySettings}
                    onConfigChange={handleConfigChange}
                  />
                </Box>
              ) : previewUrl ? (
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  <iframe
                    src={previewUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: '600px',
                      border: '1px solid #e0e0e0',
                      borderRadius: 4
                    }}
                    title="Live Template Preview"
                  />
                </Box>
              ) : (
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '600px' }}>
                  <CircularProgress />
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PrintFormatCustomizer;

